import { Trans } from '@lingui/macro';
import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@shared/hooks/useAuth';
import type { UserRole } from '@shared/types/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/ui/card';
import { Button } from '@shared/ui/button';
import { AlertTriangle, Home } from 'lucide-react';

interface RouteGuardProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
  fallback?: React.ReactNode;
}

const LoadingSkeleton: React.FC = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="animate-pulse space-y-4">
      <div className="h-8 w-48 bg-muted rounded"></div>
      <div className="h-4 w-32 bg-muted rounded"></div>
    </div>
  </div>
);

const AuthRequired: React.FC<{ requiredRoles?: UserRole[] }> = ({ requiredRoles = [] }) => {
  const location = useLocation();
  
  // Determine if this is likely a staff/admin route
  const isStaffRoute = requiredRoles.some(role => 
    ['doctor', 'admin', 'super_admin'].includes(role)
  ) || location.pathname.includes('/staff') || location.pathname.includes('/admin');
  
  // Redirect to appropriate sign-in page
  if (isStaffRoute) {
    return <Navigate to="/staff/login" state={{ from: location }} replace />;
  } else {
    return <Navigate to="/auth/patient/signin" state={{ from: location }} replace />;
  }
};



const UnauthorizedAccess: React.FC = () => {
  const navigate = useNavigate();
  
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
            <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <CardTitle><Trans id='features.auth.shared.components.RouteGuard.access-denied'>Access Denied</Trans></CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground"><Trans id='features.auth.shared.components.RouteGuard.you-don-t-have-permission'>You don't have permission to access this page.</Trans></p>
          <Button onClick={() => navigate('/dashboard', { replace: true })} variant="outline">
            <Home className="mr-2 h-4 w-4" />
            <Trans id='features.auth.shared.components.RouteGuard.home'>Home</Trans>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export const RouteGuard: React.FC<RouteGuardProps> = ({ 
  children, 
  requiredRoles = [], 
  fallback 
}) => {
  const location = useLocation();
  const { user, isLoading, isAuthenticated } = useAuth();
  
  if (isLoading) {
    return <LoadingSkeleton />;
  }
  
  if (!isAuthenticated || !user) {
    return <AuthRequired requiredRoles={requiredRoles} />;
  }
  
  if (requiredRoles.length > 0 && !requiredRoles.includes(user.role)) {
    return fallback || <UnauthorizedAccess />;
  }

  const isDoctor = user.role === "doctor";
  const isOnboardingRoute = location.pathname.startsWith("/doctor/onboarding");
  if (isDoctor && !user.onboarding_complete && !isOnboardingRoute) {
    return <Navigate to="/doctor/onboarding" state={{ from: location }} replace />;
  }
  
  return <>{children}</>;
};
